(function () {
  if (customElements.get("store-locator-mapbox")) {
    return;
  }

  class StoreLocatorMapbox extends HTMLElement {
    connectedCallback() {
      this.mapElement = this.querySelector("[data-store-locator-map]");
      this.statusElement = this.querySelector("[data-store-locator-status]");
      this.sidebarElement = this.querySelector(".store-locator-mapbox__sidebar");
      this.storeListElement = this.querySelector(".store-locator-mapbox__list");
      this.locationButton = this.querySelector("[data-store-locator-geolocate]");
      this.storeButtons = Array.from(this.querySelectorAll("[data-store-locator-trigger]"));
      this.features = this.readFeatures();
      this.markers = new Map();
      this.userMarker = null;
      this.map = null;

      this.bindStoreButtons();
      this.bindGeolocation();

      if (!this.mapElement || !this.dataset.mapboxToken || this.features.length === 0) {
        this.setMapUnavailable();
        return;
      }

      this.waitForMapbox()
        .then(() => this.initMap())
        .catch(() => this.setMapUnavailable());
    }

    disconnectedCallback() {
      if (this.map) {
        this.map.remove();
      }
    }

    readFeatures() {
      const dataElement = this.querySelector("[data-store-locator-data]");

      if (!dataElement) {
        return [];
      }

      try {
        const data = JSON.parse(dataElement.textContent);

        return (data.features || []).filter((feature) => {
          const coordinates = feature.geometry?.coordinates || [];
          const longitude = Number.parseFloat(coordinates[0]);
          const latitude = Number.parseFloat(coordinates[1]);

          if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
            return false;
          }

          feature.geometry.coordinates = [longitude, latitude];
          return true;
        });
      } catch (error) {
        return [];
      }
    }

    bindStoreButtons() {
      this.storeButtons.forEach((button) => {
        button.addEventListener("click", () => this.selectStore(button.dataset.storeId));
      });
    }

    bindGeolocation() {
      if (!this.locationButton) {
        return;
      }

      this.locationButton.addEventListener("click", () => this.focusUserLocation());
    }

    waitForMapbox() {
      return new Promise((resolve, reject) => {
        let attempts = 0;

        const checkMapbox = () => {
          if (window.mapboxgl) {
            resolve();
            return;
          }

          attempts += 1;

          if (attempts > 80) {
            reject(new Error("Mapbox GL JS did not load"));
            return;
          }

          window.setTimeout(checkMapbox, 50);
        };

        checkMapbox();
      });
    }

    initMap() {
      const center = this.getMapCenter();

      window.mapboxgl.accessToken = this.dataset.mapboxToken;

      this.map = new window.mapboxgl.Map({
        container: this.mapElement,
        style: this.dataset.mapStyle || "mapbox://styles/mapbox/streets-v12",
        center,
        zoom: Number.parseInt(this.dataset.initialZoom || "7", 10),
        cooperativeGestures: false,
        attributionControl: false,
        logoPosition: "top-left"
      });

      this.map.addControl(new window.mapboxgl.NavigationControl({ showCompass: false }), "bottom-left");
      this.map.addControl(new window.mapboxgl.AttributionControl({ compact: true }), "top-left");

      this.map.on("load", () => {
        this.mapElement.dataset.mapLoaded = "true";
        this.statusElement?.setAttribute("hidden", "");
        this.createMarkers();
        this.fitMapToStores();
      });
    }

    getMapCenter() {
      const longitude = Number.parseFloat(this.dataset.centerLongitude);
      const latitude = Number.parseFloat(this.dataset.centerLatitude);

      if (Number.isFinite(longitude) && Number.isFinite(latitude)) {
        return [longitude, latitude];
      }

      return this.features[0]?.geometry.coordinates || [5.78, 43.65];
    }

    createMarkers() {
      this.features.forEach((feature, index) => {
        const markerElement = document.createElement("button");
        markerElement.type = "button";
        markerElement.className = "store-locator-mapbox__marker";
        markerElement.dataset.storeId = feature.id;
        markerElement.setAttribute("aria-label", feature.properties.name || `Boutique ${index + 1}`);
        markerElement.addEventListener("click", () => this.selectStore(feature.id));

        const popup = new window.mapboxgl.Popup({ offset: 18 }).setHTML(this.buildPopupContent(feature));
        const marker = new window.mapboxgl.Marker(markerElement)
          .setLngLat(feature.geometry.coordinates)
          .setPopup(popup)
          .addTo(this.map);

        this.markers.set(feature.id, marker);
      });
    }

    fitMapToStores() {
      if (this.dataset.fitBounds !== "true" || this.features.length < 2) {
        return;
      }

      const bounds = new window.mapboxgl.LngLatBounds();
      this.features.forEach((feature) => bounds.extend(feature.geometry.coordinates));

      this.map.fitBounds(bounds, {
        padding: this.getCameraPadding(70),
        maxZoom: 10,
        duration: 0
      });
    }

    selectStore(storeId, options = {}) {
      if (!storeId) {
        return;
      }

      const feature = this.features.find((store) => store.id === storeId);
      let activeItem = null;

      this.storeButtons.forEach((button) => {
        const isActive = button.dataset.storeId === storeId;
        const item = button.closest(".store-locator-mapbox__item");
        button.setAttribute("aria-pressed", String(isActive));
        item?.classList.toggle("is-active", isActive);

        if (isActive) {
          activeItem = item;
        }
      });

      this.scrollStoreIntoView(activeItem, options.scroll !== false);

      this.markers.forEach((marker, markerStoreId) => {
        marker.getElement().classList.toggle("is-active", markerStoreId === storeId);
      });

      if (!feature || !this.map) {
        return;
      }

      const marker = this.markers.get(storeId);

      if (options.flyTo !== false) {
        this.map.flyTo({
          center: feature.geometry.coordinates,
          zoom: Math.max(this.map.getZoom(), 12),
          padding: this.getCameraPadding(80),
          retainPadding: false,
          essential: true
        });
      }

      marker?.getPopup()?.addTo(this.map);
    }

    scrollStoreIntoView(item, shouldAnimate) {
      if (!item || !this.storeListElement || !this.isMobileStoreSlider()) {
        return;
      }

      const targetLeft = item.offsetLeft - ((this.storeListElement.clientWidth - item.clientWidth) / 2);
      const maxLeft = Math.max(this.storeListElement.scrollWidth - this.storeListElement.clientWidth, 0);

      this.storeListElement.scrollTo({
        left: Math.min(Math.max(targetLeft, 0), maxLeft),
        behavior: shouldAnimate ? "smooth" : "auto",
      });
    }

    isMobileStoreSlider() {
      return !window.matchMedia("(min-width: 1000px)").matches;
    }

    getCameraPadding(basePadding) {
      const padding = {
        top: basePadding,
        right: basePadding,
        bottom: basePadding,
        left: basePadding
      };

      if (!this.sidebarElement) {
        return padding;
      }

      const sidebarRect = this.sidebarElement.getBoundingClientRect();

      if (window.matchMedia("(min-width: 1000px)").matches) {
        padding.right = Math.ceil(sidebarRect.width) + basePadding;
      } else {
        padding.bottom = Math.ceil(sidebarRect.height) + basePadding;
      }

      return padding;
    }

    focusUserLocation() {
      if (!navigator.geolocation) {
        this.showStatus(this.dataset.locationErrorMessage);
        return;
      }

      this.locationButton?.setAttribute("disabled", "");

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userCoordinates = [position.coords.longitude, position.coords.latitude];
          this.locationButton?.removeAttribute("disabled");

          if (!this.map) {
            this.showStatus(this.dataset.locationErrorMessage);
            return;
          }

          this.setUserMarker(userCoordinates);
          this.map.flyTo({
            center: userCoordinates,
            zoom: 11,
            padding: this.getCameraPadding(80),
            retainPadding: false,
            essential: true
          });

          const nearestFeature = this.findNearestFeature(userCoordinates);

          if (nearestFeature) {
            window.setTimeout(() => this.selectStore(nearestFeature.id), 350);
          }
        },
        () => {
          this.locationButton?.removeAttribute("disabled");
          this.showStatus(this.dataset.locationErrorMessage);
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
      );
    }

    setUserMarker(coordinates) {
      const markerElement = document.createElement("span");
      markerElement.className = "store-locator-mapbox__user-marker";

      if (this.userMarker) {
        this.userMarker.remove();
      }

      this.userMarker = new window.mapboxgl.Marker(markerElement)
        .setLngLat(coordinates)
        .addTo(this.map);
    }

    findNearestFeature(coordinates) {
      return this.features.reduce((nearestFeature, feature) => {
        const distance = this.getDistanceInKilometers(coordinates, feature.geometry.coordinates);

        if (!nearestFeature || distance < nearestFeature.distance) {
          return { ...feature, distance };
        }

        return nearestFeature;
      }, null);
    }

    getDistanceInKilometers(origin, destination) {
      const earthRadius = 6371;
      const toRadians = (degree) => degree * Math.PI / 180;
      const latitudeDelta = toRadians(destination[1] - origin[1]);
      const longitudeDelta = toRadians(destination[0] - origin[0]);
      const originLatitude = toRadians(origin[1]);
      const destinationLatitude = toRadians(destination[1]);
      const a = Math.sin(latitudeDelta / 2) ** 2 + Math.cos(originLatitude) * Math.cos(destinationLatitude) * Math.sin(longitudeDelta / 2) ** 2;

      return earthRadius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    setMapUnavailable() {
      if (this.mapElement) {
        this.mapElement.dataset.mapUnavailable = "true";
      }
    }

    showStatus(message) {
      if (!this.statusElement || !message) {
        return;
      }

      this.statusElement.textContent = message;
      this.statusElement.removeAttribute("hidden");
    }

    buildPopupContent(feature) {
      const properties = feature.properties || {};
      const directions = properties.directionsUrl
        ? `<a class="store-locator-mapbox__popup-link" href="${this.escapeAttribute(properties.directionsUrl)}" target="_blank" rel="noopener">${this.escapeHTML(this.dataset.directionsLabel || "Itineraire")}</a>`
        : "";
      const phone = properties.phone
        ? `<p class="store-locator-mapbox__popup-phone">${this.escapeHTML(properties.phone)}</p>`
        : "";

      return `
        <div class="store-locator-mapbox__popup" data-analytics-store="store_map" data-store-id="${this.escapeAttribute(feature.id || "")}" data-store-name="${this.escapeAttribute(properties.name || "")}">
          <p class="store-locator-mapbox__popup-title">${this.escapeHTML(properties.name || "")}</p>
          <p class="store-locator-mapbox__popup-address">${properties.address || ""}</p>
          ${phone}
          ${directions}
        </div>
      `;
    }

    escapeHTML(value) {
      return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    }

    escapeAttribute(value) {
      return this.escapeHTML(value).replace(/`/g, "&#096;");
    }

  }

  customElements.define("store-locator-mapbox", StoreLocatorMapbox);
})();
